import React from 'react';
import { useTheme } from 'styled-components';
import { Dropdown, Input, Spin, Tooltip, message } from 'antd';
import {
    CheckCircleOutlined,
    CheckOutlined,
    DashboardOutlined,
    EllipsisOutlined,
    FolderOutlined,
    InboxOutlined,
    PlusOutlined,
    SearchOutlined,
} from '@ant-design/icons';

import {
    FeatureHeading,
    FeatureHeadingSection,
    FeatureShell,
    FeatureShellInner,
    FeatureSidebarBodyScroll,
    FeatureSidebarNav,
    FeatureSidebarNavItem,
} from '@/components/layout/insideShell.styles';
import { FeatureSidebarAccountFooter } from '@/components/layout/FeatureSidebarAccountFooter';
import { ProjectFeatureSidebarMenu } from '@/components/module/projects/projectFeatureSidebar';
import { formatNotificationUnreadLabel } from '@hooks/useNotifications';
import {
    FeatureSidebarChatAvatar,
    FeatureSidebarChatInfo,
    FeatureSidebarChatItem,
    FeatureSidebarChatMeta,
    FeatureSidebarChatNameTwoLines,
    FeatureSidebarChatScroll,
    FeatureSidebarChatSectionLabel,
    FeatureSidebarItemCount,
    FeatureSidebarItemLabel,
    FeatureSidebarItemWithCount,
    FeatureSidebarProjectName,
    FeatureSidebarStatusText,
} from '@/layout/style';
import { getAvatarColor } from '@utils/avatarColor';
import ChatCircleAvatar from '@/components/ui/ChatCircleAvatar';
import { colors, getInsideChrome } from '@utils/colorSystem';

function OpenMarketLeadRow({ lead, L }) {
    const { location, openMarketOverviewPath, openMarketLeadIdFromUrl, navigate } = L;
    const active = location.pathname === openMarketOverviewPath && openMarketLeadIdFromUrl === lead.id;
    const s = (lead.status || '').toLowerCase();
    const statusLabel = s === 'in_progress' ? 'In Progress' : s ? s.charAt(0).toUpperCase() + s.slice(1) : '—';
    const statusColor =
        s === 'open'
            ? colors.primary[500]
            : s === 'in_progress'
                ? colors.warning[500]
                : s === 'closed'
                    ? colors.neutral[500]
                    : 'var(--ant-color-text-tertiary)';
    return (
        <FeatureSidebarChatItem
            type="button"
            $active={!!active}
            $minimal
            onClick={() =>
                navigate(openMarketOverviewPath + '?leadId=' + encodeURIComponent(lead.id))
            }
        >
            <FeatureSidebarChatAvatar
                $active={!!active}
                style={{
                    background: 'transparent',
                    ...(!active && { color: 'var(--ant-color-text-secondary)' }),
                }}
            >
                <FolderOutlined style={{ fontSize: 16 }} />
            </FeatureSidebarChatAvatar>
            <FeatureSidebarChatInfo>
                <FeatureSidebarChatNameTwoLines $active={!!active}>{lead.title || '—'}</FeatureSidebarChatNameTwoLines>
                <FeatureSidebarStatusText $color={active ? 'var(--ant-color-primary)' : statusColor}>
                    {statusLabel}
                </FeatureSidebarStatusText>
            </FeatureSidebarChatInfo>
        </FeatureSidebarChatItem>
    );
}

function InsideFeatureSidebar({ L }) {
    const theme = useTheme();
    const chrome = getInsideChrome(theme);
    const {
        featureSidebarWidth,
        moduleIdForSidebar,
        chatSidebarSearch,
        setChatSidebarSearch,
        markAllChatsAsReadMutation,
        chatSidebarUnreadIds,
        projectIdFromPath,
        projectDetailIsError,
        projectHeaderDisplayName,
        projectFeatureNavDisplayRows,
        featureSidebarTitle,
        featureSidebarSelectedKeys,
        copyValue,
        handleMenuClick,
        chatListLoading,
        chatSidebarGroups,
        openCreateChat,
        location,
        isTenantRoute,
        markChatAsReadMutation,
        buildChatActionsMenuItems,
        navigate,
        exitGroupMutation,
        clearChatConversationMutation,
        currentUserAvatarColor,
        getUserInitials,
        getUserName,
        notificationsPath,
        notificationsTotal,
        unreadCount,
        notificationsReadCount,
        openMarketOverviewPath,
        openMarketLeadIdFromUrl,
        openMarketLeadsLoading,
        openMarketMyLeads,
        openMarketMyAcceptedBidLeads,
        selectedKeys,
        featureSidebarItems,
    } = L;

    /** Inset nav (icon + label) only for modules that used the default Ant Menu; notifications / open-market keep title + separate body. */
    const showDefaultFeatureNav =
        moduleIdForSidebar !== 'notifications' && moduleIdForSidebar !== 'open-market';

    return (
        <FeatureShell $width={featureSidebarWidth}>
            <FeatureShellInner
                $chat={moduleIdForSidebar === 'chat'}
                style={
                    moduleIdForSidebar === 'chat'
                        ? { overflow: 'hidden', padding: 0 }
                        : { display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }
                }
            >
                {projectIdFromPath && moduleIdForSidebar === 'projects' ? (
                    <FeatureHeadingSection $withNav={false} style={{ paddingBottom: 12 }}>
                        <FeatureHeading>
                            {projectDetailIsError ? 'Project' : projectHeaderDisplayName || ''}
                        </FeatureHeading>
                    </FeatureHeadingSection>
                ) : projectIdFromPath ? (
                    <FeatureSidebarProjectName>
                        {projectHeaderDisplayName ||
                            (projectDetailIsError ? 'Project' : '')}
                    </FeatureSidebarProjectName>
                ) : moduleIdForSidebar === 'chat' ? (
                    <FeatureHeadingSection $withNav={true}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: -4 }}>
                            <FeatureHeading>{featureSidebarTitle}</FeatureHeading>
                            <Tooltip title="Mark all as read">
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (markAllChatsAsReadMutation.isPending) return;
                                        if (chatSidebarUnreadIds.length === 0) return;
                                        markAllChatsAsReadMutation.mutate(chatSidebarUnreadIds);
                                    }}
                                    disabled={chatSidebarUnreadIds.length === 0 || markAllChatsAsReadMutation.isPending}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        padding: '4px',
                                        cursor: 'pointer',
                                        color: chrome.featureNavIcon,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '6px',
                                        transition: 'background 0.2s',
                                        opacity: chatSidebarUnreadIds.length === 0 || markAllChatsAsReadMutation.isPending ? 0.5 : 1,
                                    }}
                                >
                                    <CheckOutlined style={{ fontSize: 15 }} />
                                </button>
                            </Tooltip>
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <Input
                                placeholder="Search..."
                                value={chatSidebarSearch}
                                onChange={(e) => setChatSidebarSearch(e.target.value)}
                                prefix={<SearchOutlined style={{ color: chrome.featureNavIcon }} />}
                                style={{
                                    borderRadius: '8px',
                                    background: chrome.featureNavHoverBg,
                                    border: 'none',
                                    height: '34px',
                                }}
                            />
                        </div>
                    </FeatureHeadingSection>
                ) : (
                    <FeatureHeadingSection $withNav={showDefaultFeatureNav}>
                        <FeatureHeading>{featureSidebarTitle}</FeatureHeading>
                        {showDefaultFeatureNav ? (
                            <FeatureSidebarNav aria-label="Section navigation">
                                {featureSidebarItems.map((item) => {
                                    const active =
                                        Array.isArray(selectedKeys) && selectedKeys.includes(item.key);
                                    return (
                                        <FeatureSidebarNavItem
                                            key={item.key}
                                            type="button"
                                            $active={active}
                                            onClick={() => handleMenuClick({ key: item.key })}
                                        >
                                            <span className="feature-nav-icon">{item.icon}</span>
                                            <span className="feature-nav-label">{item.label}</span>
                                        </FeatureSidebarNavItem>
                                    );
                                })}
                            </FeatureSidebarNav>
                        ) : null}
                    </FeatureHeadingSection>
                )}
                <FeatureSidebarBodyScroll $chat={moduleIdForSidebar === 'chat'}>
                    {projectIdFromPath && moduleIdForSidebar === 'projects' ? (
                        <div style={{ padding: '0 16px 16px 16px' }}>
                            {projectFeatureNavDisplayRows.length > 0 ? (
                                <ProjectFeatureSidebarMenu
                                    rows={projectFeatureNavDisplayRows}
                                    selectedKeys={featureSidebarSelectedKeys}
                                    onTabNavigate={(key) => handleMenuClick({ key })}
                                    onCopyLink={copyValue}
                                />
                            ) : null}
                        </div>
                    ) : moduleIdForSidebar === 'chat' ? (
                        <FeatureSidebarChatScroll style={{ flex: 1, minHeight: 0 }}>
                            {chatListLoading ? (
                                <div
                                    style={{
                                        padding: 24,
                                        textAlign: 'center',
                                        color: 'var(--ant-color-text-secondary)',
                                    }}
                                >
                                    <Spin size="small" />
                                </div>
                            ) : chatSidebarGroups.length === 0 ? (
                                <div style={{ padding: 16, color: 'var(--ant-color-text-secondary)' }}>
                                    No conversations found
                                </div>
                            ) : (
                                chatSidebarGroups.map((group) => (
                                    <div key={group.key}>
                                        <FeatureSidebarChatSectionLabel
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                paddingRight: 6,
                                            }}
                                        >
                                            <span>{group.label}</span>
                                            {group.key === 'direct' ? (
                                                <button
                                                    type="button"
                                                    onClick={() => openCreateChat('dm')}
                                                    style={{
                                                        border: 'none',
                                                        background: 'transparent',
                                                        padding: 2,
                                                        cursor: 'pointer',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                    }}
                                                    aria-label="New direct message"
                                                >
                                                    <PlusOutlined style={{ fontSize: 14, opacity: 0.75 }} />
                                                </button>
                                            ) : group.key === 'channels' ? (
                                                <button
                                                    type="button"
                                                    onClick={() => openCreateChat('group')}
                                                    style={{
                                                        border: 'none',
                                                        background: 'transparent',
                                                        padding: 2,
                                                        cursor: 'pointer',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                    }}
                                                    aria-label="New group chat"
                                                >
                                                    <PlusOutlined style={{ fontSize: 14, opacity: 0.75 }} />
                                                </button>
                                            ) : (
                                                <PlusOutlined style={{ fontSize: 14, opacity: 0.25 }} />
                                            )}
                                        </FeatureSidebarChatSectionLabel>
                                        {group.items.length === 0 ? (
                                            <div
                                                style={{
                                                    padding: '4px 16px 10px',
                                                    fontSize: 12,
                                                    color: 'var(--ant-color-text-secondary)',
                                                }}
                                            >
                                                {group.key === 'direct'
                                                    ? 'No direct messages yet'
                                                    : group.key === 'channels'
                                                        ? 'No group chats yet'
                                                        : 'No project channels yet'}
                                            </div>
                                        ) : null}
                                        {group.items.map((c) => {
                                            const id = c.id ?? c._id;
                                            const chatPath = isTenantRoute ? `/tenant/chat/${id}` : `/chat/${id}`;
                                            const active = location.pathname === chatPath;
                                            const unread = c.unreadCount ?? c.unread ?? 0;
                                            const avatarColor = getAvatarColor(id);
                                            const initials = String(c.__title || '?')
                                                .split(/\s+/)
                                                .map((s) => s[0])
                                                .join('')
                                                .toUpperCase()
                                                .slice(0, 2);
                                            const meta = c.project?.name ?? c.openMarketLead?.title ?? '';
                                            return (
                                                <FeatureSidebarChatItem
                                                    key={id}
                                                    type="button"
                                                    $active={active}
                                                    onClick={() => {
                                                        if (!active && unread > 0) {
                                                            markChatAsReadMutation.mutate(id);
                                                        }
                                                        handleMenuClick({ key: chatPath });
                                                    }}
                                                    $minimal
                                                    $flat
                                                >
                                                    <ChatCircleAvatar
                                                        imageUrl={c.__avatarImageUrl}
                                                        initials={initials}
                                                        size={28}
                                                        avatarBg={avatarColor.bg}
                                                        avatarColor={avatarColor.color}
                                                        fontSize={11}
                                                    />
                                                    <FeatureSidebarChatInfo style={{ flex: 1, minWidth: 0 }}>
                                                        <FeatureSidebarChatNameTwoLines $active={active}>
                                                            {c.__title || 'Chat'}
                                                        </FeatureSidebarChatNameTwoLines>
                                                        {meta ? (
                                                            <FeatureSidebarChatMeta>{meta}</FeatureSidebarChatMeta>
                                                        ) : null}
                                                    </FeatureSidebarChatInfo>
                                                    {unread > 0 && (
                                                        <FeatureSidebarItemCount>{unread}</FeatureSidebarItemCount>
                                                    )}
                                                    <Dropdown
                                                        trigger={['click']}
                                                        placement="bottomRight"
                                                        menu={{
                                                            items: buildChatActionsMenuItems({ ...c, __chatPath: chatPath }),
                                                            onClick: ({ key }) => {
                                                                if (key === 'search') {
                                                                    try {
                                                                        window.dispatchEvent(
                                                                            new CustomEvent('chat:open-search', {
                                                                                detail: { chatId: id ?? c?.id ?? c?._id, path: chatPath },
                                                                            }),
                                                                        );
                                                                    } catch {
                                                                        // ignore
                                                                    }
                                                                    navigate(chatPath, { state: { openChatSearch: true } });
                                                                    return;
                                                                }
                                                                if (key === 'exit') {
                                                                    exitGroupMutation.mutate(id, {
                                                                        onSuccess: () => {
                                                                            message.success('Exited group');
                                                                            try {
                                                                                window.dispatchEvent(
                                                                                    new CustomEvent('chat:exited', { detail: { chatId: id } }),
                                                                                );
                                                                            } catch {
                                                                                // ignore
                                                                            }
                                                                            // Navigate away immediately (do not wait for socket).
                                                                            navigate(isTenantRoute ? '/tenant/chat' : '/chat');
                                                                        },
                                                                        onError: (err) => {
                                                                            const msg =
                                                                                err?.response?.data?.message ??
                                                                                err?.message ??
                                                                                'Failed to exit group';
                                                                            message.error(msg);
                                                                        },
                                                                    });
                                                                    return;
                                                                }
                                                                if (key === 'star') {
                                                                    message.info('Feature coming soon');
                                                                    return;
                                                                }
                                                                if (key === 'hide') {
                                                                    message.info('Feature coming soon');
                                                                    return;
                                                                }
                                                                if (key === 'clear') {
                                                                    clearChatConversationMutation.mutate(id, {
                                                                        onSuccess: () => message.success('Conversation cleared'),
                                                                        onError: (err) => {
                                                                            const msg =
                                                                                err?.response?.data?.message ??
                                                                                err?.message ??
                                                                                'Failed to clear conversation';
                                                                            message.error(msg);
                                                                        },
                                                                    });
                                                                }
                                                            },
                                                        }}
                                                    >
                                                        <div
                                                            role="button"
                                                            tabIndex={0}
                                                            aria-label="Conversation actions"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                            }}
                                                            style={{
                                                                width: 28,
                                                                height: 28,
                                                                borderRadius: 8,
                                                                border: 'none',
                                                                background: 'transparent',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'pointer',
                                                                opacity: 0.7,
                                                            }}
                                                        >
                                                            <EllipsisOutlined style={{ transform: 'rotate(90deg)' }} />
                                                        </div>
                                                    </Dropdown>
                                                </FeatureSidebarChatItem>
                                            );
                                        })}
                                    </div>
                                ))
                            )}
                        </FeatureSidebarChatScroll>
                    ) : moduleIdForSidebar === 'notifications' ? (
                        <FeatureSidebarChatScroll>
                            {[
                                {
                                    path: notificationsPath,
                                    label: 'Unread',
                                    count: formatNotificationUnreadLabel(unreadCount ?? 0),
                                    icon: <InboxOutlined />,
                                },
                                {
                                    path: `${notificationsPath}/read`,
                                    label: 'Read',
                                    icon: <CheckCircleOutlined />,
                                },
                            ].map(({ path, label, count, icon }) => {
                                const active = location.pathname === path || (path === notificationsPath && location.pathname === `${notificationsPath}/unread`);
                                return (
                                    <FeatureSidebarItemWithCount
                                        key={path}
                                        type="button"
                                        $active={active}
                                        onClick={() => handleMenuClick({ key: path })}
                                    >
                                        {icon}
                                        <FeatureSidebarItemLabel>{label}</FeatureSidebarItemLabel>
                                        {count !== undefined && count !== null && (
                                            <FeatureSidebarItemCount>{count}</FeatureSidebarItemCount>
                                        )}
                                    </FeatureSidebarItemWithCount>
                                );
                            })}
                        </FeatureSidebarChatScroll>
                    ) : moduleIdForSidebar === 'open-market' ? (
                        <FeatureSidebarChatScroll>
                            <FeatureSidebarItemWithCount
                                type="button"
                                $active={
                                    location.pathname === openMarketOverviewPath && !openMarketLeadIdFromUrl
                                }
                                onClick={() => navigate({ pathname: openMarketOverviewPath, search: '' })}
                            >
                                <DashboardOutlined />
                                <FeatureSidebarItemLabel>Overview</FeatureSidebarItemLabel>
                                <FeatureSidebarItemCount>
                                    {openMarketLeadsLoading
                                        ? '—'
                                        : openMarketMyLeads.length + openMarketMyAcceptedBidLeads.length}
                                </FeatureSidebarItemCount>
                            </FeatureSidebarItemWithCount>
                            {openMarketLeadsLoading ? (
                                <div
                                    style={{
                                        padding: 24,
                                        textAlign: 'center',
                                        color: 'var(--ant-color-text-secondary)',
                                    }}
                                >
                                    <Spin size="small" />
                                </div>
                            ) : (
                                <>
                                    {openMarketMyLeads.length > 0 && (
                                        <>
                                            <FeatureSidebarChatSectionLabel>
                                                Leads
                                            </FeatureSidebarChatSectionLabel>
                                            {openMarketMyLeads.map((lead) => (<OpenMarketLeadRow key={lead.id} lead={lead} L={L} />))}
                                        </>
                                    )}
                                    {openMarketMyAcceptedBidLeads.length > 0 && (
                                        <>
                                            <FeatureSidebarChatSectionLabel>
                                                My accepted bids
                                            </FeatureSidebarChatSectionLabel>
                                            {openMarketMyAcceptedBidLeads.map((lead) => (<OpenMarketLeadRow key={lead.id} lead={lead} L={L} />))}
                                        </>
                                    )}
                                    {openMarketMyLeads.length === 0 &&
                                        openMarketMyAcceptedBidLeads.length === 0 && (
                                            <div
                                                style={{
                                                    padding: 12,
                                                    paddingLeft: 14,
                                                    fontSize: 13,
                                                    color: 'var(--ant-color-text-tertiary)',
                                                }}
                                            >
                                                No leads or accepted bids
                                            </div>
                                        )}
                                </>
                            )}
                        </FeatureSidebarChatScroll>
                    ) : null}
                </FeatureSidebarBodyScroll>
                <FeatureSidebarAccountFooter L={L} />
            </FeatureShellInner>
        </FeatureShell>
    );
}

export { InsideFeatureSidebar };
